import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '../stores/projectStore';
import { useAuth } from '../context/auth';
import type { Project } from '../types/project';
import RecruiterProjectCard from '../components/projects/RecruiterProjectCard';
import { useProfileRole } from '../hooks/useProfileRole';
import QuoteListCard from '../components/quotes/QuoteListCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import ProjectsHistory from '../components/projects/ProjectsHistory';
import { useLocation } from 'react-router-dom';
import SingleSubmissionModal from '../components/quotes/SingleSubmissionModal';

interface ProjectCardProps {
  project: Project;
  expanded?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, expanded = false }) => {
  const { isRecruiter } = useProfileRole();

  const cardClass = 'rounded-lg border p-4 transition duration-300';

  if (isRecruiter) {
    return (
      <div className={cardClass}>
        <RecruiterProjectCard project={project} expanded={expanded} />
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <QuoteListCard
        quote={project.quote}
        onEdit={() => {
          console.error('This is not supposed to happen: onEdit not implemented in project mode');
        }}
        expanded={expanded}
        projectMode={true}
      />
    </div>
  );
};

export default function Projects() {
  const { t } = useTranslation(['projects', 'common']);
  const { user } = useAuth();
  const { projects, fetchProjects, loading, error } = useProjectStore();

  const [selectedProject, setSelectedProject] = React.useState<Project | null>(null);

  const location = useLocation();
  const shareQuoteId = location.state?.selected_id;

  useEffect(() => {
    if (user?.id) {
      fetchProjects();
    }
  }, [user?.id, fetchProjects]);

  // Filtrage des projets par statut
  const currentProjects = projects.filter(p => p.quote.job.status === 'in_progress');
  const pastProjects = projects.filter(p => p.quote.job.status !== 'in_progress');

  const findProject = shareQuoteId
    ? projects.find(p => p.quote.id === shareQuoteId || p.quote.parent_quote_id === shareQuoteId)
    : null;

  useEffect(() => {
    if (findProject) {
      setSelectedProject(findProject);
    }
  }, [findProject]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    console.error(`Error loading projects: ${error}`);
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600">{t('errorLoadingProjects')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-8 flex flex-col gap-4 md:min-h-[calc(100vh-4rem)] md:px-5 lg:px-32">
        <div className="flex w-full flex-col pb-5 pt-4 md:w-2/3 md:px-1 md:pb-8 md:pr-8">
          <h1 className="hyphens-auto text-[3.375rem] font-bold text-secondary">
            {t('projectManager')}
          </h1>
        </div>

        <h2 className="text-sm font-semibold uppercase tracking-wider text-secondary">
          {t('myCurrentProjects')}
        </h2>

        {/* Section des projets en cours */}
        <section className="flex flex-col gap-6">
          {currentProjects.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-gray-300 py-12 text-center">
              <p className="text-lg text-gray-500">{t('noCurrentProjects')}</p>
            </div>
          ) : (
            currentProjects.map((project, index) => {
              // On définit expanded=true seulement pour le premier de la liste
              const isFirst = index === 0;
              return (
                <ProjectCard key={project.quote.job.id} project={project} expanded={isFirst} />
              );
            })
          )}
        </section>

        {/* Section Historique */}
        <div className="mt-12">
          <ProjectsHistory historyProjects={pastProjects} />
        </div>
      </div>

      {/* Modal de partage/soumission directe */}
      {selectedProject && (
        <SingleSubmissionModal
          onClose={() => setSelectedProject(null)}
          job={selectedProject.quote.job}
          quote={selectedProject.quote}
          parentQuote={selectedProject.quote}
        />
      )}
    </>
  );
}
